// StationDetail.js  ✅ UPDATED: Alerts + Recent Events moved UNDER WQI (LEFT column)
// Drop-in replacement for your current file.

//One-line explanation (for officials)

//“The WQI trend shows real-time variations in overall river water quality, while the 
// reported WQI value (45.12) represents 
// the weighted water quality status for the entire selected period.”

//Technical explanation (for validation / TORs)

//“Instantaneous WQI values were calculated at each timestamp using the US-EPA 
// weighted arithmetic method to capture short-term water quality dynamics. 
// A period-average WQI was then computed using averaged parameter concentrations to provide a 
// representative index for reporting and regulatory assessment.”

import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
  useLayoutEffect,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  FileDown,
  FileText,
  Gauge,
  Download,
  ChevronDown,
  ChevronRight,
  X,
} from "lucide-react";
import { RefreshCw } from "lucide-react";
import { createPortal } from "react-dom";
import wqmsService from "../services/wqmsService";
import parameterRanges from "../config/parameterRanges";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useToast } from "../ui/ToastProvider";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("Asia/Karachi");


function useMediaQuery(query) {
  const getMatch = () =>
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia(query).matches
      : false;

  const [matches, setMatches] = useState(getMatch);

  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);

    // Safari support
    if (mql.addEventListener) mql.addEventListener("change", onChange);
    else mql.addListener(onChange);

    setMatches(mql.matches);

    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", onChange);
      else mql.removeListener(onChange);
    };
  }, [query]);

  return matches;
}



const EXPORT_ENABLED = false;

// ======================================================
// ✅ US-EPA Weighted Arithmetic Mean WQI (Employer Req. Section 6)
// WQI = Σ(Qi*Wi) / Σ(Wi)
// Wi = (1/Si) / Σ(1/Si)
// Qi:
//  - normal (lower better): (Vi/Si)*100
//  - pH: piecewise about 7.0
//  - DO: (V0 - Vi)/(V0 - Si)*100   (Si is minimum, V0 ideal)
// Notes:
// - WQI lower is better (0 = ideal).
// - Spikes become visible because Qi can exceed 100.
// ======================================================

// ⚠️ Standards (Si) based on Employer Requirements Section 6 (as you provided earlier discussion)
const WQI_CFG = {
  dissolved_oxygen_mg_l_avg: { Si: 6.5, V0: 14.6, type: "do", label: "DO" },
  ph_avg: { Si: 8.5, V0: 7.0, type: "ph", label: "pH" },
  bod_mg_l_avg: { Si: 10, V0: 0, type: "normal", label: "BOD" },
  cod_mg_l_avg: { Si: 100, V0: 0, type: "normal", label: "COD" },
  ammonia_mg_l_avg: { Si: 32.5, V0: 0, type: "normal", label: "NH3" },
  chloride_mg_l_avg: { Si: 155, V0: 0, type: "normal", label: "Chloride" },
  tss_mg_l_avg: { Si: 100, V0: 0, type: "normal", label: "TSS" },
};

// Precompute normalized weights (Wi)
const WQI_WEIGHTS = (() => {
  const keys = Object.keys(WQI_CFG);
  const invs = keys.map((k) => 1 / (Number(WQI_CFG[k].Si) || 1));
  const sumInv = invs.reduce((a, b) => a + b, 0) || 1;
  const w = {};
  keys.forEach((k, i) => {
    w[k] = invs[i] / sumInv;
  });
  return w;
})();

const toNum = (v) => {
  if (v === null || v === undefined) return NaN;
  const n = Number(v);
  return Number.isFinite(n) ? n : NaN;
};

const isValidForWqi = (key, v) => {
  const n = toNum(v);
  if (!Number.isFinite(n)) return false;

  // ✅ pH: must be within realistic range (exclude 0 / negatives / >14)
  if (key === "ph_avg") return n > 0 && n <= 14;

  // ✅ Dissolved Oxygen: exclude 0 (missing) and unrealistic high values
  if (key === "dissolved_oxygen_mg_l_avg") return n > 0 && n <= 30;

  // ✅ All other WQI parameters (BOD, COD, NH3, Chloride, TSS etc.)
  // Treat 0 as missing/invalid so it DOES NOT reduce the WQI artificially.
  return n > 0;
};

const qiFor = (key, Vi) => {
  const cfg = WQI_CFG[key];
  const v = toNum(Vi);
  if (!cfg || !Number.isFinite(v)) return null;

  const Si = Number(cfg.Si);

  if (cfg.type === "normal") {
    return (v / (Si || 1)) * 100;
  }

  if (cfg.type === "ph") {
    const V0 = 7.0;
    const lower = 6.5;
    const upper = Si; // 8.5
    if (v === V0) return 0;

    if (v > V0) return ((v - V0) / (upper - V0 || 1)) * 100;
    return ((V0 - v) / (V0 - lower || 1)) * 100;
  }

  if (cfg.type === "do") {
    const V0 = Number(cfg.V0) || 14.6;
    return ((V0 - v) / (V0 - Si || 1)) * 100;
  }

  return null;
};

const computeWqiWeightedArithmetic = (row) => {
  let sum = 0;
  let sumW = 0;

  const details = [];

  for (const key of Object.keys(WQI_CFG)) {
    const v = row?.[key];
    if (!isValidForWqi(key, v)) continue;

    const Qi = qiFor(key, v);
    if (!Number.isFinite(Qi)) continue;

    const Wi = Number(WQI_WEIGHTS[key] ?? 0);
    if (!Number.isFinite(Wi) || Wi <= 0) continue;

    sum += Qi * Wi;
    sumW += Wi;

    details.push({
      key,
      label: WQI_CFG[key]?.label || key,
      Vi: toNum(v),
      Si: WQI_CFG[key]?.Si,
      Qi,
      Wi,
      QiWi: Qi * Wi,
    });
  }

  if (!sumW) return { wqi: null, details: [] };

  const wqi = sum / sumW;
  return { wqi, details };
};

const wqiClassLabel = (wqi) => {
  const n = toNum(wqi);
  if (!Number.isFinite(n)) return "No data";
  if (n <= 25) return "Excellent";
  if (n <= 50) return "Good";
  if (n <= 75) return "Poor";
  if (n <= 100) return "Very Poor";
  return "Unsuitable";
};

const wqiClassColor = (wqi) => {
  const n = toNum(wqi);
  if (!Number.isFinite(n)) return "#94a3b8";
  if (n <= 25) return "#16a34a";
  if (n <= 50) return "#22c55e";
  if (n <= 75) return "#f59e0b";
  if (n <= 100) return "#ef4444";
  return "#7f1d1d";
};

function AutoRefreshIcon({ refreshing }) {
  return (
    <RefreshCw
      size={16}
      color="#64748b"
      className={refreshing ? "icon-spin" : ""}
      aria-label={refreshing ? "Refreshing" : "Idle"}
    />
  );
}


const LegendItem = ({ label, value }) => (
  <div
    style={{
      display: "flex",
      gap: 8,
      alignItems: "center",
      fontSize: 12,
      color: "#475569",
      background: "rgba(241,245,249,0.7)",
      border: "1px solid rgba(226,232,240,1)",
      borderRadius: 9999,
      padding: "4px 10px",
      whiteSpace: "nowrap",
    }}
  >
    <span style={{ fontWeight: 700, color: "#0f172a" }}>{label}</span>
    <span>{value}</span>
  </div>
);

const WqiClassPill = ({ label, rangeText, color }) => (
  <div
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      padding: "4px 10px",
      borderRadius: 9999,
      border: `1px solid ${color}`,
      background: "#fff",
      fontSize: 12,
      color: "#334155",
      whiteSpace: "nowrap",
    }}
  >
    <span
      style={{
        width: 10,
        height: 10,
        borderRadius: 9999,
        background: color,
        display: "inline-block",
      }}
    />
    <span style={{ fontWeight: 700 }}>{label}</span>
    <span style={{ color: "#64748b" }}>{rangeText}</span>
  </div>
);

const isQuickRange = (timeRange, customFrom, customTo) => {
  return (
    !customFrom &&
    !customTo &&
    timeRange &&
    timeRange !== "all" &&
    !isNaN(Number(timeRange))
  );
};

function useLatestRecordTime(stationId) {
  const [latest, setLatest] = React.useState(null);

  React.useEffect(() => {
    if (!stationId) {
      setLatest(null);
      return;
    }
    const ac = new AbortController();

    wqmsService
      .getLastRecord(stationId, { signal: ac.signal })
      .then((resp) => {
        const iso = resp?.data?.last_record_utc || null;
        const display = iso
          ? iso.replace("T", " ").replace("Z", "").slice(0, 19)
          : null;
        setLatest(display);
      })
      .catch(() => setLatest(null));

    return () => ac.abort();
  }, [stationId]);

  return latest;
}

const formatValue = (value, unit = "") => {
  if (value === null || value === undefined) return "N/A";
  const n = Number(value);
  if (!Number.isFinite(n)) return "N/A";
  return `${n.toFixed(2)}${unit}`;
};

const formatTs = (ts) => {
  if (!ts) return "N/A";
  try {
    let parsed;
    if (typeof ts === "number") parsed = dayjs(ts);
    else {
      const normalized = ts.replace(" ", "T");
      parsed = dayjs(normalized).tz();
    }
    return parsed.isValid() ? parsed.format("YYYY-MM-DD HH:mm:ss") : "N/A";
  } catch {
    return "N/A";
  }
};

const safeDateValue = (v) => {
  if (!v) return null;

  if (typeof v === "number") {
    return v < 1e12 ? v * 1000 : v;
  }

  if (typeof v === "string") {
    const s = v.trim().replace(" ", "T");
    const d = dayjs.tz(s, "Asia/Karachi");
    return d.isValid() ? d.valueOf() : null;
  }

  return null;
};



const PARAM_COLORS = {
  ph_avg: "#1f77b4",
  temperature_c_avg: "#ff7f0e",
  dissolved_oxygen_mg_l_avg: "#2ca02c",
  turbidity_ntu_avg: "#d62728",
  ammonia_mg_l_avg: "#9467bd",
  bod_mg_l_avg: "#8c564b",
  cod_mg_l_avg: "#e377c2",
  tss_mg_l_avg: "#bcbd22",
  toc_mg_l_avg: "#17becf",
  doc_mg_l_avg: "#e41a1c",
  redox_mv_avg: "#377eb8",
  chloride_mg_l_avg: "#4daf4a",
  color_hazen_avg: "#984ea3",
  //level_m_avg: "#fc8d62",
  conductivity_us_cm_avg: "#1b9e77",
  wqi_calc: "#0f172a",
};

const getParamColor = (key) => PARAM_COLORS[key] || "#4f46e5";

const parameterCards = [
  { key: "ammonia_mg_l_avg", label: "Ammonia (NH3)", unit: " mg/L" },
  { key: "bod_mg_l_avg", label: "BOD", unit: " mg/L" },
  { key: "cod_mg_l_avg", label: "COD", unit: " mg/L" },
  { key: "chloride_mg_l_avg", label: "Chloride", unit: " mg/L" },
  { key: "color_hazen_avg", label: "Color", unit: " Hazen" },
  { key: "dissolved_oxygen_mg_l_avg", label: "Dissolved Oxygen", unit: " mg/L" },
  { key: "toc_mg_l_avg", label: "TOC", unit: " mg/L" },
  { key: "doc_mg_l_avg", label: "DOC", unit: " mg/L" },
  { key: "ph_avg", label: "pH", unit: "" },
  { key: "redox_mv_avg", label: "Redox/ORP", unit: " mV" },
  { key: "temperature_c_avg", label: "Temperature", unit: " °C" },
  { key: "tss_mg_l_avg", label: "TSS", unit: " mg/L" },
  { key: "turbidity_ntu_avg", label: "Turbidity", unit: " NTU" },
  //{ key: "level_m_avg", label: "Level", unit: " m" },
  { key: "conductivity_us_cm_avg", label: "Conductivity", unit: " µS/cm" },
];

const SHORT_LABELS = {
  ammonia_mg_l_avg: "NH3",
  bod_mg_l_avg: "BOD",
  cod_mg_l_avg: "COD",
  chloride_mg_l_avg: "Chloride",
  color_hazen_avg: "Color",
  dissolved_oxygen_mg_l_avg: "DO",
  toc_mg_l_avg: "TOC",
  doc_mg_l_avg: "DOC",
  ph_avg: "pH",
  redox_mv_avg: "ORP",
  temperature_c_avg: "Temp",
  tss_mg_l_avg: "TSS",
  turbidity_ntu_avg: "Turbidity",
  //level_m_avg: "Level",
  conductivity_us_cm_avg: "Cond.",
  wqi_calc: "WQI",
};

const getShortLabel = (key, fallback) => SHORT_LABELS[key] || fallback || key;

const STATUS_COLORS = {
  ok: "#16a34a",
  warn: "#f59e0b",
  crit: "#ef4444",
  neutral: "#94a3b8",
};

const StatusChip = ({ label, color }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      borderRadius: 9999,
      padding: "2px 8px",
      fontSize: 12,
      background: "#fff",
      fontWeight: 600,
      border: `1px solid ${color}`,
      color,
    }}
  >
    <span
      style={{
        width: 8,
        height: 8,
        borderRadius: 9999,
        background: color,
        display: "inline-block",
      }}
    />
    {label}
  </span>
);

const SectionWrap = ({ children }) => (
  <div
    style={{
      background: "#fff",
      border: "1px solid #e5e7eb",
      borderRadius: 16,
      padding: 14,
      boxShadow: "0 2px 8px rgba(15,23,42,0.04)",
    }}
  >
    {children}
  </div>
);


const WqiInputsCard = ({ liveRow, wqiDetails }) => {
  // wqiDetails is the `details` array returned from computeWqiWeightedArithmetic(...)
  // We'll also show parameters even if missing, with "N/A".
  const rows = Object.keys(WQI_CFG).map((key) => {
    const cfg = WQI_CFG[key];
    const wi = Number(WQI_WEIGHTS[key] ?? 0);
    const v = liveRow?.[key];

    const found = wqiDetails?.find((d) => d.key === key);
    return {
      key,
      label: cfg?.label || key,
      type: cfg?.type || "normal",
      Si: cfg?.Si ?? null,
      Wi: Number.isFinite(wi) ? wi : null,
      Vi: Number.isFinite(toNum(v)) ? Number(v) : null,
      Qi: Number.isFinite(toNum(found?.Qi)) ? Number(found.Qi) : null,
      QiWi: Number.isFinite(toNum(found?.QiWi)) ? Number(found.QiWi) : null,
      used: !!found, // only true if value was valid & included
    };
  });

  const fmt = (n, d = 2) => (Number.isFinite(Number(n)) ? Number(n).toFixed(d) : "—");

  const typeText = (t) => {
    if (t === "do") return "Higher is better (DO deficit from ideal)";
    if (t === "ph") return "Deviation from 7.0 (piecewise)";
    return "Lower is better (Vi/Si × 100)";
    };

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 16,
        padding: 12,
        width: "100%",
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ fontWeight: 800, color: "#0f172a" }}>
          WQI Inputs & Standards (Weighted Arithmetic)
        </div>
        <div style={{ fontSize: 12, color: "#64748b" }}>
          Parameters included only if valid in the selected time window.
        </div>
      </div>

      <div style={{ marginTop: 6, fontSize: 12, color: "#64748b", lineHeight: 1.35 }}>
        <div><b>Si</b> = standard/limit used • <b>Wi</b> = normalized weight (1/Si) • <b>Qi</b> computed per parameter type • <b>Qi·Wi</b> contribution</div>
      </div>

      <div style={{ overflowX: "auto", marginTop: 10 }}>
        <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
          <thead>
            <tr>
              {["Used", "Parameter", "Type", "Standard (Si)", "Weight (Wi)", "Value used (Vo)", "Qi", "Qi·Wi"].map((h) => (
                <th
                  key={h}
                  style={{
                    textAlign: "left",
                    fontSize: 12,
                    color: "#475569",
                    padding: "10px 10px",
                    borderBottom: "1px solid #e5e7eb",
                    background: "#f8fafc",
                    whiteSpace: "nowrap",
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {rows.map((r) => (
              <tr key={r.key}>
                <td style={{ padding: "10px 10px", borderBottom: "1px solid #eef2f7" }}>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: 12,
                      fontWeight: 700,
                      color: r.used ? "#16a34a" : "#94a3b8",
                    }}
                  >
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: 9999,
                        background: r.used ? "#22c55e" : "#cbd5e1",
                        display: "inline-block",
                      }}
                    />
                    {r.used ? "Yes" : "No"}
                  </span>
                </td>

                <td style={{ padding: "10px 10px", borderBottom: "1px solid #eef2f7", fontWeight: 700, color: "#0f172a" }}>
                  {r.label}
                  
                </td>

                <td style={{ padding: "10px 10px", borderBottom: "1px solid #eef2f7", color: "#334155" }}>
                  <div style={{ fontWeight: 700, fontSize: 12 }}>{r.type.toUpperCase()}</div>
                  <div style={{ fontSize: 11, color: "#64748b" }}>{typeText(r.type)}</div>
                </td>

                <td style={{ padding: "10px 10px", borderBottom: "1px solid #eef2f7" }}>{fmt(r.Si, 2)}</td>
                <td style={{ padding: "10px 10px", borderBottom: "1px solid #eef2f7" }}>{fmt(r.Wi, 6)}</td>
                <td style={{ padding: "10px 10px", borderBottom: "1px solid #eef2f7" }}>{fmt(r.Vi, 2)}</td>
                <td style={{ padding: "10px 10px", borderBottom: "1px solid #eef2f7" }}>{fmt(r.Qi, 2)}</td>
                <td style={{ padding: "10px 10px", borderBottom: "1px solid #eef2f7" }}>{fmt(r.QiWi, 2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 10, fontSize: 12, color: "#64748b" }}>
        <b>Note:</b> If a parameter is missing/invalid (e.g., DO=0, pH=0), it is excluded from WQI for this window.
      </div>
    </div>
  );
};



const GroupPill = ({ text, color }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      padding: "5px 12px",
      borderRadius: 9999,
      fontSize: 13,
      fontWeight: 700,
      border: `1px solid ${color}`,
      color,
      background: "#fff",
    }}
  >
    <span
      style={{
        width: 10,
        height: 10,
        borderRadius: 9999,
        background: color,
        opacity: 0.8,
        display: "inline-block",
      }}
    />
    {text}
  </span>
);

const ChevronCircleBtn = ({ collapsed, onClick, label }) => (
  <button
    aria-label={label}
    onClick={onClick}
    style={{
      width: 38,
      height: 28,
      borderRadius: 9999,
      border: "1px solid #e5e7eb",
      background: "#fff",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
    }}
  >
    {collapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
  </button>
);

const GaugeChart = ({
  value,
  min = 0,
  max = 14,
  label = "pH",
  goodMin,
  goodMax,
}) => {
  const dmin = Math.min(Number(min), Number(max));
  const dmax = Math.max(Number(min), Number(max));
  const safe = Number.isFinite(Number(value)) ? Number(value) : NaN;

  const clampedValue = Number.isFinite(safe)
    ? Math.max(dmin, Math.min(dmax, safe))
    : dmin;
  const actualPct = (clampedValue - dmin) / (dmax - dmin || 1);
  const actualAngle = 180 + actualPct * 180;

  const r = 52,
    cx = 70,
    cy = 90;
  const rad = (actualAngle * Math.PI) / 180;
  const rn = r * 0.85;
  const x = cx + rn * Math.cos(rad);
  const y = cy + rn * Math.sin(rad);

  let reverseColors = false;
  if (Number.isFinite(goodMin) && Number.isFinite(goodMax)) {
    const gLo = Math.min(goodMin, goodMax);
    const gHi = Math.max(goodMin, goodMax);
    const gCenter = (gLo + gHi) / 2;
    const mid = dmin + (dmax - dmin) / 2;
    reverseColors = gCenter < mid;
  }

  let needleColor = "#334155";
  if (Number.isFinite(safe) && Number.isFinite(goodMin) && Number.isFinite(goodMax)) {
    const gLo = Math.min(goodMin, goodMax);
    const gHi = Math.max(goodMin, goodMax);
    needleColor = safe >= gLo && safe <= gHi ? "#16a34a" : "#ef4444";
  }

  const gid = `g_${label.replace(/\W+/g, "")}_${Math.random()
    .toString(36)
    .slice(2, 7)}`;

  const leftColor = reverseColors ? "#16a34a" : "#ef4444";
  const rightColor = reverseColors ? "#ef4444" : "#16a34a";

  return (
    <svg width="140" height="110" viewBox="0 0 140 110">
      <defs>
        <linearGradient id={gid} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={leftColor} />
          <stop offset="50%" stopColor="#eab308" />
          <stop offset="100%" stopColor={rightColor} />
        </linearGradient>
      </defs>

      <path
        d="M18 90 A52 52 0 0 1 122 90"
        fill="none"
        stroke={`url(#${gid})`}
        strokeWidth="12"
        strokeLinecap="round"
      />

      <circle cx={cx} cy={cy} r="2.5" fill="#475569" />

      {Number.isFinite(x) && Number.isFinite(y) ? (
        <line
          x1={cx}
          y1={cy}
          x2={x}
          y2={y}
          stroke={needleColor}
          strokeWidth="1.1"
          strokeLinecap="round"
        />
      ) : (
        <line
          x1={cx}
          y1={cy}
          x2={cx}
          y2={cy - r * 0.8}
          stroke={needleColor}
          strokeWidth="1.1"
          strokeLinecap="round"
        />
      )}

      <text x="70" y="106" textAnchor="middle" fontSize="11" fill="#334155">
        {formatValue(value)}
        <tspan x="70" dy="12">
          {label}
        </tspan>
      </text>
    </svg>
  );
};

// ===== MultiLineChart (unchanged) =====
const MultiLineChart = ({ data, series, xKey = "timestamp", quickRange = false }) => {

  const W = 820,
    H = 220,
    PAD = 36;
  const [hover, setHover] = useState(null);
  const svgRef = useRef(null);

  const toSvgXY = (evt) => {
    const svg = svgRef.current;
    const pt = svg.createSVGPoint();
    pt.x = evt.clientX;
    pt.y = evt.clientY;
    const { x, y } = pt.matrixTransform(svg.getScreenCTM().inverse());
    return { x, y };
  };

  const prepared = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) {
      return {
        paths: [],
        px: [],
        timestamps: [],
        valuesBySeries: {},
        alignedPtsBySeries: {},
        minX: 0,
        maxX: 1,
        globalMinY: 0,
        globalMaxY: 1,
        sx: () => 0,
        sy: () => 0,
      };
    }

    const maxPoints = 160;
    const step = Math.ceil(data.length / maxPoints);
    const sampled = step > 1 ? data.filter((_, i) => i % step === 0) : data;

  const xs = sampled
  .map((d) => safeDateValue(d[xKey], quickRange))
  .filter(Number.isFinite);

    if (xs.length === 0) {
      return {
        paths: [],
        px: [],
        timestamps: [],
        valuesBySeries: {},
        alignedPtsBySeries: {},
        minX: 0,
        maxX: 1,
        globalMinY: 0,
        globalMaxY: 1,
        sx: () => 0,
        sy: () => 0,
      };
    }

    let minX = Math.min(...xs);
    let maxX = Math.max(...xs);
    if (!Number.isFinite(minX) || !Number.isFinite(maxX) || minX === maxX) {
      minX -= 60000;
      maxX += 60000;
    }

    const sx = (t) =>
      PAD + ((t - minX) / (maxX - minX || 1)) * (W - 2 * PAD);
    const px = xs.map(sx);

    let globalMinY = Infinity,
      globalMaxY = -Infinity;
    const valuesBySeries = {};
    const alignedPtsBySeries = {};

    for (const s of series) {
      const ys = sampled.map((d) => Number(d[s.key]));
      valuesBySeries[s.key] = ys;
      for (const v of ys) {
        if (Number.isFinite(v)) {
          if (v < globalMinY) globalMinY = v;
          if (v > globalMaxY) globalMaxY = v;
        }
      }
    }

    if (!Number.isFinite(globalMinY) || !Number.isFinite(globalMaxY)) {
      globalMinY = 0;
      globalMaxY = 1;
    }
    if (globalMinY === globalMaxY) {
      globalMinY -= 1;
      globalMaxY += 1;
    }

    const sy = (v) =>
      H -
      PAD -
      ((v - globalMinY) / (globalMaxY - globalMinY || 1)) * (H - 2 * PAD);

    const paths = series
      .map((s) => {
        const aligned = sampled.map((d, i) => {
          const xv = px[i];
          const yv = sy(Number(d[s.key]));
          return Number.isFinite(xv) && Number.isFinite(yv) ? [xv, yv] : null;
        });
        alignedPtsBySeries[s.key] = aligned;

        let dstr = "";
        let run = [];
        const flush = () => {
          if (run.length < 2) {
            run = [];
            return;
          }
          let seg = `M ${run[0][0]} ${run[0][1]}`;
          for (let i = 1; i < run.length; i++) {
            const [x0, y0] = run[i - 1];
            const [x1, y1] = run[i];
            const cx = x0 + (x1 - x0) * 0.5;
            const cy = y0 + (y1 - y0) * 0.5;
            seg += ` Q ${cx} ${cy} ${x1} ${y1}`;
          }
          dstr += (dstr ? " " : "") + seg;
          run = [];
        };

        for (const p of aligned) (p ? run.push(p) : flush());
        flush();

        return dstr
          ? { color: s.color, d: dstr, key: s.key, label: s.label, unit: s.unit }
          : null;
      })
      .filter(Boolean);

    return {
      paths,
      px,
      timestamps: xs,
      valuesBySeries,
      alignedPtsBySeries,
      minX,
      maxX,
      globalMinY,
      globalMaxY,
      sx,
      sy,
    };
  }, [data, series, xKey]);

  const handleMove = (e) => {
    const { x, y } = toSvgXY(e);
    if (!prepared.px.length) return;
    if (x < PAD || x > W - PAD || y < PAD || y > H - PAD) {
      setHover(null);
      return;
    }
    let best = 0,
      bestDist = Infinity;
    for (let i = 0; i < prepared.px.length; i++) {
      const d = Math.abs(prepared.px[i] - x);
      if (d < bestDist) {
        best = i;
        bestDist = d;
      }
    }
    setHover({ x: prepared.px[best], i: best, y });
  };
  const handleLeave = () => setHover(null);

  const xTicks = useMemo(() => {
    const n = prepared.timestamps.length;
    if (!n) return [];
    const idxs = Array.from(
      new Set([
        0,
        Math.floor(n * 0.25),
        Math.floor(n * 0.5),
        Math.floor(n * 0.75),
        n - 1,
      ])
    )
      .filter((i) => i >= 0 && i < n)
      .sort((a, b) => a - b);

    return idxs.map((i) => ({
      x: prepared.px[i],
      label: dayjs(prepared.timestamps[i])
  .tz("Asia/Karachi")
  .format("HH:mm:ss"),

    }));
  }, [prepared.px, prepared.timestamps]);

  const yTicks = useMemo(() => {
    const ticks = 4,
      arr = [];
    for (let i = 0; i <= ticks; i++) {
      const v =
        prepared.globalMinY +
        (i / ticks) * (prepared.globalMaxY - prepared.globalMinY);
      const y = prepared.sy ? prepared.sy(v) : 0;
      arr.push({ y, label: Number.isFinite(v) ? v.toFixed(3) : "0" });
    }
    return arr;
  }, [prepared.globalMinY, prepared.globalMaxY, prepared.sy]);

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      height={H}
      style={{ background: "#fff" }}
      shapeRendering="geometricPrecision"
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
    >
      <rect x="0" y="0" width={W} height={H} fill="#ffffff" />
      <path d={`M ${PAD} ${H - PAD} L ${W - PAD} ${H - PAD}`} stroke="#e5e7eb" />
      <path d={`M ${PAD} ${PAD} L ${PAD} ${H - PAD}`} stroke="#e5e7eb" />

      {xTicks.map((t, idx) => (
        <g key={idx}>
          <path
            d={`M ${t.x} ${H - PAD} L ${t.x} ${H - PAD + 4}`}
            stroke="#cbd5e1"
          />
          <text
            x={t.x}
            y={H - PAD + 16}
            fontSize="10"
            fill="#64748b"
            textAnchor="middle"
          >
            {t.label}
          </text>
        </g>
      ))}

      {yTicks.map((t, idx) => (
        <g key={idx}>
          <path d={`M ${PAD - 4} ${t.y} L ${W - PAD} ${t.y}`} stroke="#f1f5f9" />
          <text
            x={PAD - 8}
            y={t.y + 3}
            fontSize="10"
            fill="#64748b"
            textAnchor="end"
          >
            {t.label}
          </text>
        </g>
      ))}

      {prepared.paths.map((p, i) => (
        <path
          key={i}
          d={p.d}
          fill="none"
          stroke={p.color}
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      ))}

      {hover && Number.isFinite(hover.x) && (
        <>
          <line
            x1={hover.x}
            y1={PAD}
            x2={hover.x}
            y2={H - PAD}
            stroke="#94a3b8"
            strokeDasharray="4 3"
          />
          {prepared.paths.map((p, idx) => {
            const pt = prepared.alignedPtsBySeries[p.key]?.[hover.i];
            if (!pt || !Number.isFinite(pt[0]) || !Number.isFinite(pt[1]))
              return null;
            return (
              <circle
                key={idx}
                cx={pt[0]}
                cy={pt[1]}
                r="3"
                fill={p.color}
                stroke="#fff"
                strokeWidth="1"
              />
            );
          })}

          {(() => {
            const ts = prepared.timestamps?.[hover.i];
			const yVals = prepared.paths.map((p) => ({
			  color: p.color,
			  value: prepared.valuesBySeries[p.key]?.[hover.i],
			  label: p.label,
			  unit: p.unit,
			}));

			const label = dayjs(ts).isValid()
			  ? dayjs(ts).format("YYYY-MM-DD HH:mm:ss")
			  : "N/A";

			// ✅ If too many items, split into 2 columns
			const cols = yVals.length > 8 ? 2 : 1;
			const rowsPerCol = Math.ceil(yVals.length / cols);

			// Dimensions
			const rowH = 16;
			const headerH = 34;
			const padding = 10;
			const paddingTop = 10;


			const colW = 200;                 // width of one column
			const boxW = cols === 1 ? 200 : (colW * cols + 20); // +gap space
			const boxH = headerH + paddingTop  + rowsPerCol * rowH + 10;

			// ✅ Clamp X/Y so tooltip stays inside SVG
			const boxX = Math.min(Math.max(hover.x + 8, PAD), W - PAD - boxW);
			const desiredY = (Number.isFinite(hover.y) ? hover.y : (PAD + 6)) - boxH / 2;
			const boxY = Math.min(Math.max(desiredY, PAD), H - PAD - boxH);

			return (
  <g>
    <rect
      x={boxX}
      y={boxY}
      width={boxW}
      height={boxH}
      rx="8"
      fill="#ffffff"
      stroke="#e5e7eb"
    />
    <text x={boxX + 10} y={boxY + 20} fontSize="12" fontWeight="700" fill="#0f172a">
      {label}
    </text>

    {/* Divider line under date */}
    <path
      d={`M ${boxX + 10} ${boxY + headerH - 8} L ${boxX + boxW - 10} ${boxY + headerH - 8}`}
      stroke="#e5e7eb"
    />

    {yVals.map((v, i) => {
      const col = Math.floor(i / rowsPerCol);
      const row = i % rowsPerCol;

      const x0 = boxX + 10 + col * (colW + 20);
      const y0 = boxY + headerH + paddingTop + row * rowH;

      return (
        <g key={i}>
          <circle cx={x0} cy={y0 - 4} r="4" fill={v.color} />
          <text x={x0 + 10} y={y0} fontSize="12" fill="#334155">
            {(v.label ? v.label + ": " : "") +
              (Number.isFinite(v.value) ? Number(v.value).toFixed(3) : "--") +
              (v.unit || "")}
          </text>
        </g>
      );
    })}
  </g>
);


			
          })()}
        </>
      )}
    </svg>
  );
};

const StationDetail = () => {
	 const isMobile = useMediaQuery("(max-width: 768px)");
  const isSmall  = useMediaQuery("(max-width: 480px)");
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [station, setStation] = useState(null);
  const [stationHistory, setStationHistory] = useState([]);
  const [trendData, setTrendData] = useState([]);

  const [timeRange, setTimeRange] = useState(() => {
    const r = new URLSearchParams(window.location.search).get("range");
    return r || "1440";
  });

  const [autoRefreshInterval, setAutoRefreshInterval] = useState("300");
  const [selectedParams, setSelectedParams] = useState(["wqi_calc"]);
  const [collapsedGroups, setCollapsedGroups] = useState({
    critical: false,
    warning: false,
    warning_min: false,
    warning_max: false,
    info: false,
  });
  const [statusFilter, setStatusFilter] = useState("all");
  const [exportOpen, setExportOpen] = useState(false);
  const exportRef = useRef(null);

  const [timeMenuOpen, setTimeMenuOpen] = useState(false);
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const live = useMemo(() => {
    if (!stationHistory?.length) return station;
    const avg = {};
    for (const key of Object.keys(stationHistory[0])) {
      if (key.endsWith("_clean") || key.endsWith("_avg")) {
        const vals = stationHistory
          .map((r) => Number(r[key]))
          .filter((v) => !isNaN(v));
        avg[key] = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
      }
    }
    return { ...station, ...avg };
  }, [station, stationHistory]);

  const computeWindow = useCallback(() => {
  // Absolute range: send from/to (UTC ISO)
  if (customFrom && customTo) {
    const fromDay = dayjs.tz(customFrom, "Asia/Karachi").startOf("day");
    const toDay = dayjs.tz(customTo, "Asia/Karachi").endOf("day");

    return {
      tr: undefined,
      start: fromDay.toISOString(), // UTC instant
      end: toDay.toISOString(),     // UTC instant
    };
  }

  // Quick range: send ONLY timeRange
  if (timeRange !== "all" && !isNaN(Number(timeRange))) {
    return {
      tr: String(timeRange),
      start: undefined,
      end: undefined,
    };
  }

  // All time
  return { tr: "all", start: undefined, end: undefined };
}, [timeRange, customFrom, customTo]);


  const fetchStation = useCallback(
    async (silent = false, abortSignal) => {
      if (silent) setIsRefreshing(true);
      else setLoading(true);

      setError("");

      try {
        const { tr, start, end } = computeWindow();

        const r = await wqmsService.getStationData(id, {
          timeRange: tr,
          at: undefined,
          from: start,
          to: end,
          fallback: true,
          signal: abortSignal,
        });

        if (!r?.success) throw new Error(r?.message || "Failed to load station");

        const historyRaw = Array.isArray(r.data?.history)
          ? r.data.history
          : r.data?.history
          ? [r.data.history]
          : [];

        const normalized = historyRaw
          .filter(Boolean)
          .map((item) => {
            const row = {
              ...item,
              timestamp:
                item.timestamp ||
                item.latest_record_time ||
                item.created_at ||
                item.time ||
                null,
            };

            const { wqi } = computeWqiWeightedArithmetic(row);
            row.wqi_calc = Number.isFinite(toNum(wqi)) ? Number(wqi) : null;
            return row;
          })
          .sort(
            (a, b) =>
              (safeDateValue(b.timestamp) ?? 0) - (safeDateValue(a.timestamp) ?? 0)
          );

        if (r.data?.station) setStation(r.data.station);
        else if (!station) setStation(null);

        setStationHistory(normalized);

        if (normalized[0]) {
          setStation((s) => ({ ...(s || {}), ...normalized[0] }));
        }
      } catch (e) {
        setError(e.message || "Network error");
      } finally {
        if (silent) setIsRefreshing(false);
        else setLoading(false);
      }
    },
    [id, computeWindow]
  );

  const fetchTrend = useCallback(
  async (silent = false, abortSignal) => {
    try {
      let tr;
      let from;
      let to;

      // ✅ Absolute date range (PKT day boundaries → UTC instants)
      if (customFrom && customTo) {
        const fromDay = dayjs.tz(customFrom, "Asia/Karachi").startOf("day");
        const toDay = dayjs.tz(customTo, "Asia/Karachi").endOf("day");

        from = fromDay.toISOString();
        to = toDay.toISOString();
        tr = undefined; // IMPORTANT
      }
      // ✅ Quick range: send ONLY timeRange, not from/to
      else if (timeRange !== "all" && !isNaN(Number(timeRange))) {
        tr = String(timeRange);
        from = undefined;
        to = undefined;
      }
      // ✅ All time
      else {
        tr = "all";
        from = undefined;
        to = undefined;
      }

      const res = await wqmsService.getTrendData(
        id,
        tr,
        undefined,
        from,
        to,
        { signal: abortSignal }
      );

      if (!res.success) throw new Error(res.message || "Failed to load trend data");

      const normalized = (res.data || []).map((item) => {
        const row = {
          ...item,
          timestamp:
            item.timestamp ||
            item.latest_record_time ||
            item.created_at ||
            item.time ||
            null,
        };

        const { wqi } = computeWqiWeightedArithmetic(row);
        row.wqi_calc = Number.isFinite(toNum(wqi)) ? Number(wqi) : null;
        return row;
      });

      setTrendData(normalized);
    } catch (e) {
      console.error("Trend data error:", e);
    }
  },
  [id, timeRange, customFrom, customTo]
);


  useEffect(() => {
    const controller = new AbortController();
    fetchStation(false, controller.signal);
    fetchTrend(true, controller.signal);
    return () => controller.abort();
  }, [fetchStation, fetchTrend]);

  useEffect(() => {
    if (autoRefreshInterval === "off") return;
    const ms = Number(autoRefreshInterval) * 1000;

    const iid = setInterval(() => {
      const ac = new AbortController();
      fetchStation(true, ac.signal);
      fetchTrend(true, ac.signal);
    }, ms);

    return () => clearInterval(iid);
  }, [autoRefreshInterval, fetchStation, fetchTrend]);

  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set("range", timeRange);
    url.searchParams.set("params", selectedParams.join(","));
    window.history.replaceState({}, "", url);
  }, [timeRange, selectedParams]);

  useEffect(() => {
    const url = new URL(window.location.href);
    const r = url.searchParams.get("range");
    const p = url.searchParams.get("params");
    if (r) setTimeRange(r);
    if (p) setSelectedParams(p.split(",").filter(Boolean));
  }, []);

  const latestLocal = useLatestRecordTime(station?.station_id || id);

  const lastUpdated = station?.timestamp ? new Date(station.timestamp) : null;
  const liveStatusColor = (() => {
    if (!lastUpdated) return STATUS_COLORS.neutral;
    const ageSec = (Date.now() - lastUpdated.getTime()) / 1000;
    if (ageSec <= 120) return STATUS_COLORS.ok;
    if (ageSec <= 600) return STATUS_COLORS.warn;
    return STATUS_COLORS.crit;
  })();

  const getRangeForParam = useCallback(
    (key) => {
      const conf = parameterRanges[key];
      if (conf) {
        if (conf.range) {
          const r0 = Number(conf.range[0]);
          const r1 = Number(conf.range[1]);
          return { min: Math.min(r0, r1), max: Math.max(r0, r1) };
        }
        const g = conf.good;
        const w = conf.warn;
        const min = Math.min(g[0], g[1], w[0], w[1]);
        const max = Math.max(g[0], g[1], w[0], w[1]);
        return { min, max };
      }
      const vals = stationHistory.map((h) => Number(h[key])).filter(Number.isFinite);
      if (vals.length) {
        const min = Math.min(...vals);
        const max = Math.max(...vals);
        const pad = (max - min) * 0.1 || 1;
        return { min: min - pad, max: max + pad };
      }
      return { min: 0, max: 1 };
    },
    [stationHistory]
  );

  const getStatusColor = (key, v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "#94a3b8";
    if (key === "wqi_calc") return wqiClassColor(n);

    const conf = parameterRanges[key];
    if (!conf) return "#4f46e5";
    const [gmin, gmax] = conf.good;
    const [wmin, wmax] = conf.warn;
    const inGood = n >= Math.min(gmin, gmax) && n <= Math.max(gmin, gmax);
    const inWarn = n >= Math.min(wmin, wmax) && n <= Math.max(wmin, wmax);
    if (inGood) return STATUS_COLORS.ok;
    if (inWarn) return STATUS_COLORS.warn;
    return STATUS_COLORS.crit;
  };

  const getFriendlyStatusLabel = (key, value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return "No data";
    if (key === "wqi_calc") return wqiClassLabel(n);

    const conf = parameterRanges[key];
    if (!conf) return "OK";
    const [gmin, gmax] = conf.good;
    const [wmin, wmax] = conf.warn;
    const goodMin = Math.min(gmin, gmax),
      goodMax = Math.max(gmin, gmax);
    const warnMin = Math.min(wmin, wmax),
      warnMax = Math.max(wmin, wmax);
    const inGood = n >= goodMin && n <= goodMax;
    const inWarn = n >= warnMin && n <= warnMax && !inGood;
    if (inGood) return "OK";
    if (inWarn) return "Caution";
    return n < goodMin ? "Low" : "Exceeded";
  };

  const compactParams = parameterCards;

 const wqiComputed = useMemo(() => {
  if (!live) return { wqi: null, details: [] };
  return computeWqiWeightedArithmetic(live);
}, [live]);

const wqiValue = useMemo(() => {
  const w = wqiComputed?.wqi;
  return Number.isFinite(toNum(w)) ? Number(w) : null;
}, [wqiComputed]);


  const alerts = useMemo(() => {
    const list = [];
    if (!stationHistory || !stationHistory.length) return list;
    const last = stationHistory[0];
    for (const p of parameterCards) {
      const conf = parameterRanges[p.key];
      if (!conf) continue;
      const val = Number(last[p.key]);
      if (!Number.isFinite(val)) continue;
      const [gmin, gmax] = conf.good;
      const [wmin, wmax] = conf.warn;
      const inGood = val >= Math.min(gmin, gmax) && val <= Math.max(gmin, gmax);
      const inWarn = val >= Math.min(wmin, wmax) && val <= Math.max(wmin, wmax);
      if (!inGood && !inWarn) {
        list.push({
          type: "critical",
          key: p.key,
          param: p.label,
          value: val,
          unit: p.unit,
          rule: `> ${Math.max(gmax, gmin)} or < ${Math.min(gmax, gmin)}`,
        });
      } else if (!inGood && inWarn) {
        const isBelow = val < Math.min(gmin, gmax);
        const subtype = isBelow ? "warning_min" : "warning_max";
        list.push({
          type: subtype,
          key: p.key,
          param: p.label,
          value: val,
          unit: p.unit,
          rule: `Warn band ${Math.min(wmin, wmax)}–${Math.max(wmin, wmax)}`,
        });
      }
    }
    return list;
  }, [stationHistory]);

  useEffect(() => {
    try {
      localStorage.setItem("alertsCollapsed", JSON.stringify(collapsedGroups));
    } catch {}
  }, [collapsedGroups]);

  useEffect(() => {
    try {
      const s = localStorage.getItem("alertsCollapsed");
      if (s) setCollapsedGroups(JSON.parse(s));
    } catch {}
  }, []);

  const events = useMemo(() => {
    const ev = [];
    if (!stationHistory || stationHistory.length < 2) return ev;
    const keys = ["ph_avg", "temperature_c_avg", "turbidity_ntu_avg"];
    for (let i = 1; i < stationHistory.length; i++) {
      const prev = stationHistory[i - 1];
      const curr = stationHistory[i];
      keys.forEach((key) => {
        const a = Number(prev[key]);
        const b = Number(curr[key]);
        if (!Number.isFinite(a) || !Number.isFinite(b)) return;
        const delta = b - a;
        const abs = Math.abs(delta);
        const threshold =
          key === "ph_avg" ? 0.5 : key === "temperature_c_avg" ? 2 : 10;
        if (abs >= threshold) {
          const label =
            parameterCards.find((p) => p.key === key)?.label ||
            key.replace("_avg", "");
          const unit = parameterCards.find((p) => p.key === key)?.unit || "";
          const category = delta > 0 ? "sudden spike" : "sudden drop";
          const dirSign = delta > 0 ? "+" : "−";
          ev.push({
            ts: curr.timestamp,
            key,
            label,
            unit,
            category,
            text: `${category}: ${label} ${dirSign}${abs.toFixed(2)}${unit}`,
            beforeValue: a,
            afterValue: b,
            change: delta,
          });
        }
      });
    }
    return ev.slice(0, 10);
  }, [stationHistory]);

  const exportCSV = async () => {
    try {
      const stationId = station?.station_id;
      if (!stationId) {
        toast.error("No station selected", { title: "Station Selection" });
        return;
      }
      const { tr, start, end } = computeWindow();
      await wqmsService.exportCSV(stationId, tr ?? "5", undefined, start, end);
    } catch (err) {
      console.error("Error exporting CSV:", err);
      toast.error("Failed to export CSV.", { title: "CSV Export" });
    }
  };

  const timeMenuRef = useRef(null);
  const [menuPos, setMenuPos] = useState({ top: 100, left: 8, width: 320 });
  const M = 8;

  const recomputeMenuPos = useCallback(() => {
    const anchorRect = timeMenuRef.current?.getBoundingClientRect();
    const menuWidth = Math.min(540, window.innerWidth - M * 2);
    const desiredLeft = anchorRect?.left ?? M;
    const clampedLeft = Math.max(
      M,
      Math.min(desiredLeft, window.innerWidth - menuWidth - M)
    );
    const desiredTop = (anchorRect?.bottom ?? 100) + 8;
    const clampedTop = Math.max(M, Math.min(desiredTop, window.innerHeight - M));
    setMenuPos({ top: clampedTop, left: clampedLeft, width: menuWidth });
  }, []);

  useLayoutEffect(() => {
    if (!timeMenuOpen) return;
    recomputeMenuPos();
    const onChange = () => recomputeMenuPos();
    window.addEventListener("resize", onChange);
    window.addEventListener("scroll", onChange, true);
    return () => {
      window.removeEventListener("resize", onChange);
      window.removeEventListener("scroll", onChange, true);
    };
  }, [timeMenuOpen, recomputeMenuPos]);

  return (
    <>
      {isRefreshing && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            height: 3,
            width: "100%",
            background: "linear-gradient(90deg, #3b82f6, #22c55e, #3b82f6)",
            backgroundSize: "200% 100%",
            animation: "loadingBar 1s linear infinite",
            zIndex: 9999,
          }}
        />
      )}

      <div className="App neutral-page-bg" style={{ minHeight: "100vh" }}>
        <div
          className="wqms-dashboard"
          style={{background: "transparent" }}
        >
          {/* ===== HEADER (UNCHANGED) ===== */}
          <div
            style={{
              background: "#f9fafb",
              borderBottom: "1px solid #e5e7eb",
              position: "sticky",
              top: 0,
              zIndex: 20,
            }}
          >
            {/* TOP BAR */}
            <div
			  style={{
				maxWidth: 1280,
				margin: "0 auto",
				padding: isSmall ? "10px 12px" : "10px 24px",
				display: "grid",
				gridTemplateColumns: isMobile ? "1fr" : "repeat(12, 1fr)",
				gap: isMobile ? 12 : 24,
				alignItems: "center",
			  }}
			>
<div
  style={{
    gridColumn: isMobile ? "1 / -1" : "span 8 / span 8",
    display: "flex",
    alignItems: "center",
    gap: 12,
    minWidth: 0,
    flexWrap: "wrap",
  }}
>

                <Gauge size={22} color="#334155" />
                <div>
                  <div style={{ fontSize: 24, fontWeight: 600, color: "#0f172a" }}>
                    {station?.site_name || "Station"}
                  </div>
                  <div style={{ fontSize: 13, color: "#64748b" }}>
                    {station?.district || ""}
                  </div>
                </div>

                <div
                  style={{
                    marginLeft: 16,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: 12,
                    color: "#475569",
                  }}
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 9999,
                      background: liveStatusColor,
                      display: "inline-block",
                    }}
                  />
                </div>

                <div
                  style={{
                    marginLeft: 16,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    background: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: 12,
                    padding: "2px 6px",
                  }}
                >
                  <AutoRefreshIcon refreshing={isRefreshing} />
                  <select
                    value={autoRefreshInterval}
                    onChange={(e) => setAutoRefreshInterval(e.target.value)}
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: 8,
                      padding: "2px 6px",
                      height: 24,
                      lineHeight: "20px",
                      fontSize: 12,
                      background: "#fff",
                      cursor: "pointer",
                    }}
                    aria-label="Auto refresh interval"
                  >
                    <option value="off">Off</option>
                    <option value="15">15s</option>
                    <option value="30">30s</option>
                    <option value="300">5m</option>
                  </select>
                </div>

                <div
                  style={{
                    marginLeft: 8,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: 12,
                    background: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: 8,
                    padding: "4px 8px",
                    position: "relative",
                  }}
                  ref={timeMenuRef}
                >
                  Time range:
                  <button
                    className="param-chip"
                    onClick={() => setTimeMenuOpen((v) => !v)}
                    aria-haspopup="menu"
                    aria-expanded={timeMenuOpen}
                  >
                    {(() => {
                      if (customFrom && customTo) {
                        const fromDate = new Date(customFrom);
                        return `Custom range (${fromDate.toLocaleDateString()})`;
                      } else if (timeRange !== "all" && !isNaN(Number(timeRange))) {
                        const m = Number(timeRange);
                        const now = new Date();
                        const start = new Date(now.getTime() - m * 60 * 1000);
                        let desc = "";
                        if (m < 60) desc = `Last ${m} minutes`;
                        else if (m % 60 === 0 && m < 1440)
                          desc = `Last ${m / 60} hour${m === 60 ? "" : "s"}`;
                        else if (m % 1440 === 0)
                          desc = `Last ${m / 1440} day${m === 1440 ? "" : "s"}`;
                        else desc = `Last ${m} minutes`;
                        return `${desc} (${start.toLocaleDateString()})`;
                      }
                      return "All time";
                    })()}
                  </button>

                  {timeMenuOpen &&
                    createPortal(
                      <>
                        <div
                          style={{
                            position: "fixed",
                            inset: 0,
                            background: "rgba(0,0,0,0.1)",
                            zIndex: 9998,
                          }}
                          onClick={() => setTimeMenuOpen(false)}
                        />

                        <div
                          style={{
                            position: "fixed",
                            top: menuPos.top,
                            left: menuPos.left,
                            width: menuPos.width,
                            maxWidth: "calc(100vw - 16px)",
                            maxHeight: `calc(100vh - ${menuPos.top + M}px)`,
                            overflowY: "auto",
                            background: "#fff",
                            border: "1px solid #e5e7eb",
                            borderRadius: 12,
                            padding: 12,
                            boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                            gap: 12,
                            zIndex: 9999,
                            animation: "fadeInUp 0.15s ease-out",
                          }}
                        >
                          <div>
                            <div style={{ fontWeight: 700, marginBottom: 6 }}>
                              Quick ranges
                            </div>
                            {[
                              { value: 60, label: "Last 1 hour" },
                              { value: 120, label: "Last 2 hours" },
                              { value: 180, label: "Last 3 hours" },
                              { value: 240, label: "Last 4 hours" },
                              { value: 300, label: "Last 5 hours" },
                              { value: 360, label: "Last 6 hours" },
                              { value: 720, label: "Last 12 hours" },
                              { value: 1440, label: "Last 24 hours" },
                              { value: 43200, label: "Last 30 days" },
                            ].map((r) => (
                              <button
                                key={r.value}
                                className="param-chip"
                                style={{
                                  display: "block",
                                  width: "100%",
                                  marginBottom: 6,
                                  textAlign: "left",
                                }}
                                onClick={() => {
                                  setCustomFrom("");
                                  setCustomTo("");
                                  setTimeRange(String(r.value));
                                  setTimeMenuOpen(false);
                                }}
                              >
                                {r.label}
                              </button>
                            ))}
                          </div>

                          <div>
                            <div style={{ fontWeight: 700, marginBottom: 6 }}>
                              Absolute date range
                            </div>

                            <div
                              style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}
                            >
                              From
                            </div>
                            <input
                              type="date"
                              value={customFrom}
                              onChange={(e) => setCustomFrom(e.target.value)}
                              style={{
                                width: "100%",
                                border: "1px solid #e5e7eb",
                                borderRadius: 8,
                                padding: "6px 8px",
                                marginBottom: 8,
                              }}
                            />

                            <div
                              style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}
                            >
                              To
                            </div>
                            <input
                              type="date"
                              value={customTo}
                              onChange={(e) => setCustomTo(e.target.value)}
                              style={{
                                width: "100%",
                                border: "1px solid #e5e7eb",
                                borderRadius: 8,
                                padding: "6px 8px",
                                marginBottom: 8,
                              }}
                            />

                            <div style={{ display: "flex", gap: 8 }}>
                              <button className="logout-btn" onClick={() => setTimeMenuOpen(false)}>
                                Close
                              </button>
                              <button
                                className="logout-btn"
                                onClick={() => {
                                  if (
                                    !customFrom ||
                                    !customTo ||
                                    new Date(customFrom) > new Date(customTo)
                                  ) {
                                    toast.warn("Please choose a valid start and end date.", {
                                      title: "Start & End Dates",
                                    });
                                    return;
                                  }
                                  setTimeRange("all");
                                  setTimeMenuOpen(false);
                                }}
                              >
                                Apply time range
                              </button>
                            </div>

                            <div style={{ fontSize: 12, color: "#64748b", marginTop: 8 }}>
                              Browser Time: {Intl.DateTimeFormat().resolvedOptions().timeZone}
                            </div>
                          </div>
                        </div>
                      </>,
                      document.body
                    )}
                </div>
              </div>

              <div
  className="wqms-toolbar"
  style={{
    gridColumn: isMobile ? "1 / -1" : "span 4 / span 4",
    justifySelf: isMobile ? "stretch" : "end",
  }}
>
                <div className="actions-bar">
                  <button className="btn btn-ghost" onClick={() => window.location.href = "https://appbackup.pakistanwqi.com/dashboard"} aria-label="Back">
                    <ArrowLeft size={16} />
                    <span>Back</span>
                  </button>

                  {EXPORT_ENABLED && (
                    <div className="actions-group">
                      <button className="btn" onClick={exportCSV} aria-label="Export CSV" title="Export CSV">
                        <FileDown size={16} />
                        <span>CSV</span>
                      </button>

                      <button className="btn" aria-label="Export PDF" title="Export PDF">
                        <FileText size={16} />
                        <span>PDF</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* STATUS FILTER */}
           <div
  style={{
    maxWidth: 1280,
    margin: "0 auto",
    padding: isSmall ? "8px 12px 10px 12px" : "8px 24px 10px 24px",
    borderTop: "1px solid #eef2f7",
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "repeat(12, 1fr)",
    gap: isMobile ? 12 : 24,
  }}
>

              <div
  style={{
    gridColumn: isMobile ? "1 / -1" : "span 8 / span 8",
    display: "flex",
    alignItems: "center",
    gap: 12,
    minWidth: 0,
    flexWrap: "wrap",
  }}
>

                <span style={{ fontSize: 12, color: "#64748b" }}>Status:</span>
                <button className={`param-chip ${statusFilter === "all" ? "active" : ""}`} onClick={() => setStatusFilter("all")}>
                  All
                </button>
                <button className={`param-chip ${statusFilter === "ok" ? "active" : ""}`} onClick={() => setStatusFilter("ok")}>
                  OK
                </button>
                <button className={`param-chip ${statusFilter === "warn" ? "active" : ""}`} onClick={() => setStatusFilter("warn")}>
                  Caution
                </button>
                <button className={`param-chip ${statusFilter === "crit" ? "active" : ""}`} onClick={() => setStatusFilter("crit")}>
                  Critical
                </button>
              </div>
              <div
  style={{
    gridColumn: isMobile ? "1 / -1" : "span 4 / span 4",
    display: "flex",
    justifyContent: isMobile ? "flex-start" : "flex-end",
    gap: 8,
  }}
/>

            </div>
          </div>

          {/* ===== BODY ===== */}
          <div style={{ maxWidth: 1280, margin: "0 auto", padding: isSmall ? 12 : 24 }}>

            {error && (
              <div
                style={{
                  marginBottom: 12,
                  color: "#991b1b",
                  background: "#fee2e2",
                  border: "1px solid #fecaca",
                  borderRadius: 12,
                  padding: "10px 14px",
                }}
              >
                {error}
              </div>
            )}

            {loading && !station ? (
              <div style={{ padding: 20 }}>Loading station...</div>
            ) : station ? (
              <>
                {/* EXPORT MODAL (unchanged) */}
                {exportOpen && (
                  <div
                    role="dialog"
                    aria-modal="true"
                    style={{
                      position: "fixed",
                      inset: 0,
                      background: "rgba(15,23,42,0.4)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 50,
                    }}
                  >
                    <div
                      style={{
                        width: "90%",
                        maxWidth: 980,
                        maxHeight: "80vh",
                        overflow: "auto",
                        background: "#fff",
                        border: "1px solid #e5e7eb",
                        borderRadius: 12,
                        padding: 16,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          marginBottom: 12,
                        }}
                      >
                        <div style={{ fontSize: 18, fontWeight: 600 }}>Export Preview</div>
                        <button className="param-chip" onClick={() => setExportOpen(false)} aria-label="Close export">
                          <X size={16} />
                        </button>
                      </div>

                      <div ref={exportRef} className="card">
                        <div className="meta">
                          <div style={{ fontSize: 13 }}>
                            <div>
                              <strong>Station:</strong> {station.site_name}
                            </div>
                            <div>
                              <strong>Location:</strong> {station.district}
                            </div>
                          </div>
                          <div style={{ fontSize: 13 }}>
                            <div>
                              <strong>Generated:</strong> {new Date().toLocaleDateString()}
                            </div>
                          </div>
                        </div>

                        <table>
                          <thead>
                            <tr>
                              {[
                                "Timestamp",
                                "WQI",
                                "pH",
                                "Temperature (°C)",
                                "Dissolved Oxygen (mg/L)",
                                "Turbidity (NTU)",
                                "Ammonia (mg/L)",
                                "Nitrate (mg/L)",
                                "COD (mg/L)",
                                "BOD (mg/L)",
                                "Chloride (mg/L)",
                                "Color (Hazen)",
                              ].map((h) => (
                                <th
                                  key={h}
                                  style={{
                                    textAlign: "left",
                                    padding: "8px 10px",
                                    borderBottom: "1px solid #e5e7eb",
                                  }}
                                >
                                  {h}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {stationHistory.map((row, idx) => (
                              <tr key={idx}>
                                <td style={{ padding: "6px 10px", borderBottom: "1px solid #eef2f7" }}>
                                  {formatTs(row.timestamp)}
                                </td>
                                <td style={{ padding: "6px 10px", borderBottom: "1px solid #eef2f7" }}>
                                  {Number.isFinite(toNum(row.wqi_calc)) ? Number(row.wqi_calc).toFixed(2) : "—"}
                                </td>
                                <td style={{ padding: "6px 10px", borderBottom: "1px solid #eef2f7" }}>
                                  {Number(row.ph_avg).toFixed(2)}
                                </td>
                                <td style={{ padding: "6px 10px", borderBottom: "1px solid #eef2f7" }}>
                                  {Number(row.temperature_c_avg).toFixed(1)}
                                </td>
                                <td style={{ padding: "6px 10px", borderBottom: "1px solid #eef2f7" }}>
                                  {Number(row.dissolved_oxygen_mg_l_avg).toFixed(2)}
                                </td>
                                <td style={{ padding: "6px 10px", borderBottom: "1px solid #eef2f7" }}>
                                  {Number(row.turbidity_ntu_avg).toFixed(2)}
                                </td>
                                <td style={{ padding: "6px 10px", borderBottom: "1px solid #eef2f7" }}>
                                  {Number(row.ammonia_mg_l_avg).toFixed(2)}
                                </td>
                                <td style={{ padding: "6px 10px", borderBottom: "1px solid #eef2f7" }}>
                                  {formatValue(row.nitrate_mg_l_avg)}
                                </td>
                                <td style={{ padding: "6px 10px", borderBottom: "1px solid #eef2f7" }}>
                                  {Number(row.cod_mg_l_avg).toFixed(2)}
                                </td>
                                <td style={{ padding: "6px 10px", borderBottom: "1px solid #eef2f7" }}>
                                  {Number(row.bod_mg_l_avg).toFixed(2)}
                                </td>
                                <td style={{ padding: "6px 10px", borderBottom: "1px solid #eef2f7" }}>
                                  {Number(row.chloride_mg_l_avg).toFixed(2)}
                                </td>
                                <td style={{ padding: "6px 10px", borderBottom: "1px solid #eef2f7" }}>
                                  {Number(row.color_hazen_avg).toFixed(2)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
                        <button
                          className="logout-btn"
                          onClick={exportCSV}
                          style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                        >
                          <Download size={16} /> Download CSV
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* =========================================================
                    ✅ LAYOUT FIX:
                    - LEFT column contains: Cards -> Trend -> WQI -> Alerts/Events
                    - RIGHT column kept empty (or for future use)
                   ========================================================= */}
                <div
                  className="station-layout"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr",
                    gap: 24,
                    alignItems: "start",
                    width: "100%",
                  }}
                >
                  {/* LEFT COLUMN */}
  <div style={{ width: "100%", minWidth: 0 }}>


                    {/* CARDS */}
                    <div className="param-grid">
                      {compactParams
                        .filter((p) => {
                          if (statusFilter === "all") return true;
                          const color = getStatusColor(p.key, live?.[p.key]);
                          if (statusFilter === "ok") return color === STATUS_COLORS.ok;
                          if (statusFilter === "warn") return color === STATUS_COLORS.warn;
                          if (statusFilter === "crit") return color === STATUS_COLORS.crit;
                          return true;
                        })
                        .map((p) => {
                          const val = live?.[p.key];
                          const color = getStatusColor(p.key, val);
                          const [gmin, gmax] = parameterRanges[p.key]?.good || [];
                          const range = getRangeForParam(p.key);
                          const borderCol =
                            color === STATUS_COLORS.ok
                              ? STATUS_COLORS.ok
                              : color === STATUS_COLORS.warn
                              ? STATUS_COLORS.warn
                              : color === STATUS_COLORS.crit
                              ? STATUS_COLORS.crit
                              : "#e5e7eb";

                          return (
                            <div
                              key={p.key}
                              style={{
                                background: "#f3f4f6",
                                border: "1px solid #e5e7eb",
                                borderLeft: `4px solid ${borderCol}`,
                                borderRadius: 12,
                                padding: 12,
                              }}
                            >
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                                <div
                                  style={{
                                    fontWeight: 700,
                                    color: "#111827",
                                    fontSize: 14,
                                    whiteSpace: "nowrap",
                                    textOverflow: "ellipsis",
                                    overflow: "hidden",
                                  }}
                                >
                                  {p.label}
                                </div>
                                <StatusChip label={getFriendlyStatusLabel(p.key, val)} color={color} />
                              </div>

                              <div style={{ display: "flex", justifyContent: "center", marginBottom: 4 }}>
                                <GaugeChart
                                  key={`${p.key}-${range.min}-${range.max}-${Number(val)}`}
                                  value={val}
                                  min={range.min}
                                  max={range.max}
                                  goodMin={gmin}
                                  goodMax={gmax}
                                  label={getShortLabel(p.key, p.label)}
                                />
                              </div>

                              <div
                                style={{
                                  fontSize: 12,
                                  color: "#64748b",
                                  textAlign: "center",
                                  display: "flex",
                                  justifyContent: "center",
                                  gap: 8,
                                  flexWrap: "wrap",
                                }}
                              >
                                <span
                                  style={{
                                    background: "#ffffff",
                                    border: "1px solid #e5e7eb",
                                    borderRadius: 8,
                                    padding: "2px 8px",
                                  }}
                                >
                                  Range: {parameterRanges[p.key]?.rangeLabel || `${range.min}–${range.max}`}
                                </span>
                                <span
                                  style={{
                                    background: "#ffffff",
                                    border: "1px solid #e5e7eb",
                                    borderRadius: 8,
                                    padding: "2px 8px",
                                  }}
                                >
                                  Good:{" "}
                                  {parameterRanges[p.key]?.goodLabel ||
                                    (Number.isFinite(gmin) && Number.isFinite(gmax)
                                      ? `${Math.min(gmin, gmax)}–${Math.max(gmin, gmax)}`
                                      : "—")}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                    </div>

                    {/* TREND */}
                    <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 16, padding: 12, marginTop: 16 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                        <div style={{ fontWeight: 700, color: "#1f2937" }}>Trend</div>
                        <div style={{ marginLeft: "auto", color: "#64748b", fontSize: 12 }}>
                          Hover to see timestamp & values
                        </div>
                      </div>
					  
<div style={{ overflowX: "auto" }}>
  <div style={{ minWidth: 820 }}>
                      <MultiLineChart
                        data={(trendData.length ? trendData : stationHistory).slice().reverse()}
                        series={selectedParams.map((k) => ({
                          key: k,
                          color: getParamColor(k),
                          label: k === "wqi_calc" ? "WQI" : parameterCards.find((p) => p.key === k)?.label || k,
                          unit: k === "wqi_calc" ? "" : parameterCards.find((p) => p.key === k)?.unit || "",
                        }))}
                      />
                      </div>
                      </div>

                      <div
  className="param-chip-row"
  style={{
    marginTop: 8,
    display: "flex",
    gap: 6,
    flexWrap: isMobile ? "nowrap" : "wrap",
    overflowX: isMobile ? "auto" : "visible",
    WebkitOverflowScrolling: "touch",
    paddingBottom: isMobile ? 6 : 0,
  }}
>

                        <button
                          type="button"
                          className={`param-chip ${selectedParams.includes("wqi_calc") ? "active" : ""}`}
                          onClick={() =>
                            setSelectedParams((prev) =>
                              prev.includes("wqi_calc") ? prev.filter((k) => k !== "wqi_calc") : [...prev, "wqi_calc"]
                            )
                          }
                        >
                          <span style={{ width: 8, height: 8, borderRadius: 9999, background: getParamColor("wqi_calc") }} />
                          WQI (EPA Weighted)
                        </button>

                        {parameterCards.map(({ key, label }) => {
                          const active = selectedParams.includes(key);
                          return (
                            <button
                              key={key}
                              type="button"
                              className={`param-chip ${active ? "active" : ""}`}
                              onClick={() =>
                                setSelectedParams((prev) =>
                                  prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
                                )
                              }
                            >
                              <span style={{ width: 8, height: 8, borderRadius: 9999, background: getParamColor(key) }} />
                              {label || key}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* WQI (UNDER TREND) */}
                    <div style={{ marginTop: 10 }}>
                      <div
  style={{
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 16,
    padding: 12,
    marginBottom: 6,
    width: "100%",
  }}
>
  {/* Header row */}
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
    <div style={{ fontWeight: 700 }}>WQI (US-EPA Weighted Arithmetic)</div>
    <StatusChip label={wqiClassLabel(wqiValue)} color={wqiClassColor(wqiValue)} />
  </div>

  {/* Value */}
  <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginTop: 6, flexWrap: "wrap" }}>
    <div style={{ fontSize: 22, fontWeight: 600, color: "#0f172a" }}>
      {Number.isFinite(toNum(wqiValue)) ? Number(wqiValue).toFixed(2) : "—"}
    </div>
    <div style={{ fontSize: 12, color: "#64748b" }}>
      Lower is better. Computed as Σ(Qi·Wi)/Σ(Wi), Wi based on 1/Si.
    </div>
  </div>

  {/* ✅ Weighted Arithmetic Legend */}
  <div style={{ marginTop: 10 }}>
    <div style={{ fontSize: 12, fontWeight: 800, color: "#334155", marginBottom: 8 }}>
      Weighted Arithmetic Legend
    </div>

    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      <LegendItem label="Qi" value="Quality rating of each parameter at time t" />
      <LegendItem label="Si" value="Standard/limit" />
      <LegendItem label="Wi" value="Weight = (1/Si) normalized across parameters" />
      <LegendItem label="WQI(t)" value="Instant WQI per record (Trend line)" />
      <LegendItem label="Period WQI" value="Computed from averaged parameters in window" />
    </div>

    {/* ✅ Class bands */}
    <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
      <WqiClassPill label="Excellent" rangeText="0–25" color="#16a34a" />
      <WqiClassPill label="Good" rangeText="26–50" color="#22c55e" />
      <WqiClassPill label="Poor" rangeText="51–75" color="#f59e0b" />
      <WqiClassPill label="Very Poor" rangeText="76–100" color="#ef4444" />
      <WqiClassPill label="Unsuitable" rangeText="> 100" color="#7f1d1d" />
    </div>
  </div>
</div>

                    </div>


                    <div style={{ marginTop: 10, width: "100%" }}>
  <WqiInputsCard liveRow={live} wqiDetails={wqiComputed.details} />
</div>



                    {/* ✅ Alerts + Recent Events UNDER WQI (LEFT COLUMN) */}
                    <div style={{ marginTop: 6 }}>
                      <SectionWrap>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                          <div style={{ fontSize: 18, fontWeight: 800, color: "#1f2937" }}>Alerts</div>
                          <div style={{ fontSize: 14, color: "#64748b" }}>Total {alerts.length}</div>
                        </div>

                        <div
                          style={{
                            background: "rgba(255,255,255,0.55)",
                            border: "1px solid rgba(226,232,240,0.8)",
                            borderRadius: 16,
                            padding: 10,
                          }}
                        >
                          {[
                            { sev: "critical", label: "Exceeded", color: STATUS_COLORS.crit },
                            { sev: "warning_max", label: "Close to maximum", color: STATUS_COLORS.warn },
                            { sev: "warning_min", label: "Close to minimum", color: STATUS_COLORS.warn },
                          ].map(({ sev, label, color }) => {
                            const group = alerts.filter((a) => a.type === sev);
                            if (!group.length) return null;

                            const collapsed = !!collapsedGroups[sev];

                            return (
                              <div
                                key={sev}
                                style={{
                                  background: "rgba(248,250,252,0.55)",
                                  border: "1px solid rgba(226,232,240,0.9)",
                                  borderRadius: 16,
                                  padding: 10,
                                  marginBottom: 12,
                                }}
                              >
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                  <GroupPill text={`${label} (${group.length})`} color={color} />
                                  <ChevronCircleBtn
                                    collapsed={collapsed}
                                    onClick={() => setCollapsedGroups((s) => ({ ...s, [sev]: !collapsed }))}
                                    label={collapsed ? `Expand ${label}` : `Collapse ${label}`}
                                  />
                                </div>

                                {!collapsed && (
                                  <div style={{ marginTop: 10 }}>
                                    {group.map((a, idx) => (
                                      <div
                                        key={`${a.key}-${idx}`}
                                        style={{
                                          display: "grid",
                                          gridTemplateColumns: "14px 1fr auto",
                                          gap: 10,
                                          alignItems: "center",
                                          padding: "10px 10px",
                                          borderRadius: 14,
                                          background: "rgba(255,255,255,0.75)",
                                          marginBottom: 8,
                                        }}
                                      >
                                        <span
                                          style={{
                                            width: 10,
                                            height: 10,
                                            borderRadius: 9999,
                                            background: color,
                                            opacity: 0.85,
                                            display: "inline-block",
                                          }}
                                        />
                                        <div style={{ minWidth: 0 }}>
                                          <div
                                            style={{
                                              fontSize: 14,
                                              fontWeight: 700,
                                              color: "#1f2937",
                                              whiteSpace: "nowrap",
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                            }}
                                          >
                                            {a.param}
                                            <span style={{ fontWeight: 500, color: "#475569" }}>
                                              {" "}
                                              — {Number(a.value).toFixed(2)}
                                              {a.unit}
                                            </span>
                                          </div>

                                          <div style={{ marginTop: 2 }}>
                                            <span
                                              style={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                padding: "2px 10px",
                                                borderRadius: 9999,
                                                fontSize: 12,
                                                color: "#64748b",
                                                background: "rgba(241,245,249,0.85)",
                                                border: "1px solid rgba(226,232,240,1)",
                                              }}
                                            >
                                              {sev === "critical"
                                                ? a.rule
                                                : `Warn band ${parameterRanges?.[a.key]?.warnLabel || a.rule}`}
                                            </span>
                                          </div>
                                        </div>

                                        <div style={{ fontSize: 13, color: "#94a3b8", paddingRight: 6 }}>now</div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        <div style={{ marginTop: 16 }}>
                          <div style={{ fontSize: 18, fontWeight: 800, color: "#1f2937" }}>Recent Events</div>
                          {events.length === 0 ? (
                            <div style={{ marginTop: 6, fontSize: 14, color: "#64748b" }}>
                              No notable changes in this window.
                            </div>
                          ) : (
                            <div style={{ marginTop: 10 }}>
                              {events.slice(0, 8).map((e, idx) => (
                                <div
                                  key={idx}
                                  style={{
                                    background: "rgba(255,255,255,0.75)",
                                    border: "1px solid rgba(226,232,240,0.9)",
                                    borderRadius: 14,
                                    padding: 10,
                                    marginBottom: 8,
                                  }}
                                >
                                  <div style={{ fontWeight: 700, color: "#1f2937" }}>{e.text}</div>
                                  <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
                                    {formatTs(e.ts)} • Before: {Number(e.beforeValue).toFixed(2)}
                                    {e.unit} → After: {Number(e.afterValue).toFixed(2)}
                                    {e.unit}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </SectionWrap>
                    </div>
                  </div>

                  {/* RIGHT COLUMN (empty now) */}
                  
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
};

export default StationDetail;
